import { useContext, useEffect, useRef } from 'react';
import {
    TyreRepairRequest,
    TyreDamageTypeOption,
    TyreSpecificErrors,
    nonUSTyreDamageTypes,
    defaultTyreDamageTypes
} from './TyreRepairRequestTypes';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import SelectInput, {
    SelectInputHelper,
    SelectOption
} from '../../common/selectInput/SelectInput';
import FormInput from '../../common/FormInput';
import { t } from 'i18next';
import { TranslationHelper } from 'classes/TranslationHelper';
import DateInput from '../../common/dateInput/DateInput';
import { TyreRepairRequestContext } from '../../../context/TyreRepairRequestContext';
import { useTranslation } from 'react-i18next';
import ImageUploader from 'components/common/fileUpload/ImageUploader';
import Images from '../../../images/TyreSpecificFormImages';
import { Locations, TyreRepairOptions } from '../../../Constants/Common';
import { UploadedDocument, UploadedImage } from '../../common/CommonTypes';
import {
    documentToImageFormat,
    imageToDocumentFormat
} from '../../common/helper';
import DocumentUploader from '../../common/fileUpload/DocumentUploader';
import TextArea from '../../common/textArea/TextArea';
import BaseAlert, { AlertId, AlertTypes } from '../../common/alerts/BaseAlert';

export type TyreSpecificFormProps = {
    repairRequest: TyreRepairRequest;
    formErrors?: TyreSpecificErrors;
    onChange: (key: keyof TyreRepairRequest, value: string) => void;
    onChangeDamageType: (selectionName: string) => void;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    onPoliceReportChange: (report: UploadedDocument) => void;
    errorSummaryTitle: string;
};

const TyreSpecificForm = ({
    repairRequest,
    formErrors,
    onChange,
    onUploadImageChange,
    onPoliceReportChange,
    errorSummaryTitle
}: TyreSpecificFormProps): JSX.Element => {
    const { i18n } = useTranslation();
    const isNonUsUser = i18n.language?.toLowerCase() !== Locations.Us;
    const helpTextDistance = t('tyre_repair_request.help_text.distance');
    const { onChangDateOfIncident } = useContext(TyreRepairRequestContext);

    const onChangeEvent = (
        selectedValue: string,
        key: keyof TyreRepairRequest
    ) => {
        onChange(key, selectedValue);
    };

    const modalBottom = useRef(null);

    useEffect(() => {
        if (formErrors !== undefined && Object.keys(formErrors).length !== 0) {
            modalBottom.current.focus();
        }
    }, [formErrors]);

    const getTyreDamageOptions = (): TyreDamageTypeOption[] => {
        const tyreOptions: TyreDamageTypeOption[] = [];

        const damageTypesToConsider = isNonUsUser
            ? nonUSTyreDamageTypes
            : defaultTyreDamageTypes;

        Object.keys(damageTypesToConsider).forEach((tyreDamage) => {
            const option = {
                id: damageTypesToConsider[tyreDamage],
                text: TranslationHelper.getTranslatedText(
                    damageTypesToConsider[tyreDamage]
                ),
                value: damageTypesToConsider[tyreDamage],
                disabled: false
            };
            tyreOptions.push(option);
        });

        return tyreOptions;
    };

    const tyreDamageTypeOptions: TyreDamageTypeOption[] =
        getTyreDamageOptions();

    const tyreRepairOptions: SelectOption[] = [
        {
            id: 'repair-option-1',
            text: t('tyre_repair_request.repair_option_1'),
            value: TyreRepairOptions.Dealer
        },
        {
            id: 'repair-option-2',
            text: t('tyre_repair_request.repair_option_2'),
            value: TyreRepairOptions.KwikFit
        },
        {
            id: 'repair-option-3',
            text: t('tyre_repair_request.repair_option_3'),
            value: TyreRepairOptions.PreferredGarage
        }
    ];

    const tyreRepairMessage = {
        [TyreRepairOptions.Dealer]: t(
            'tyre_repair_request.repair_option_message_dealership'
        ),
        [TyreRepairOptions.KwikFit]: t(
            'tyre_repair_request.repair_option_message_kwik'
        ),
        [TyreRepairOptions.PreferredGarage]: t(
            'tyre_repair_request.repair_option_message_garage'
        )
    }[repairRequest?.repairOption];

    const maxDate = () => {
        const dt = new Date();
        dt.setDate(dt.getDate());
        return dt;
    };

    const errorlist = (): Array<string | undefined> => {
        const errorValues = [
            formErrors?.dateOfIncident,
            formErrors?.damageType,
            formErrors?.damageTypeComment,
            formErrors?.policeReport,
            formErrors?.damageSize,
            formErrors?.closeUpImage,
            formErrors?.distanceImage
        ];

        const usErrorValues = [formErrors?.damageType, formErrors?.damageSize];

        const errors = isNonUsUser
            ? errorValues.filter((error) => error !== undefined)
            : usErrorValues.filter((error) => error !== undefined);

        return errors;
    };

    const onChangeDate = (selectedIncidentDate: string): void => {
        if (selectedIncidentDate !== null) {
            onChangDateOfIncident(selectedIncidentDate);
        }
    };

    const onPoliceReportUpdate = (_, __, image: UploadedImage) => {
        onPoliceReportChange(imageToDocumentFormat(image));
    };

    return (
        <div className='card page-content no-border'>
            <div className='card-body page-content-body'>
                <FormInput
                    id='incident-date-forminput'
                    label={t('incident_date')}
                    errorText={formErrors?.dateOfIncident}>
                    <DateInput
                        name='dateOfIncidentDatePicker'
                        useJson
                        forceTimeReset
                        value={repairRequest?.dateOfIncident}
                        onDateChanged={(dateString) => onChangeDate(dateString)}
                        maximumDate={maxDate()}
                    />
                </FormInput>
                <FormInput
                    id='damage-type-forminput'
                    label={t('tyre_repair_request.step_2_damage_type_title')}
                    helpText={t('help_text.size_of_damage')}
                    errorText={
                        formErrors?.damageType ?? formErrors?.damageTypeComment
                    }>
                    <SelectInput
                        name='damage-size'
                        loadingText={t('loading')}
                        value={repairRequest?.damageType}
                        options={tyreDamageTypeOptions}
                        onChange={(selectedValue: string) =>
                            onChangeEvent(selectedValue, 'damageType')
                        }
                        forceDefault
                        defaultIfOneOption
                        addDefaultOption
                        defaultText={t('select_default')}
                    />
                    {isNonUsUser &&
                        repairRequest?.damageType ===
                            nonUSTyreDamageTypes.Other && (
                            <TextArea
                                value={repairRequest?.damageTypeComment}
                                name='comment'
                                containerClassName='mt-3'
                                innerClassName='fullscreen'
                                label={t('please_state')}
                                maxLength={1000}
                                onChange={(e) =>
                                    onChangeEvent(
                                        e.currentTarget.value,
                                        'damageTypeComment'
                                    )
                                }
                            />
                        )}
                </FormInput>
                {isNonUsUser && (
                    <FormInput
                        id='damage-type-forminput'
                        label={t('tyre_repair_request.repair_option_label')}>
                        <SelectInput
                            name='repair-option'
                            loadingText={t('loading')}
                            value={repairRequest?.repairOption}
                            options={tyreRepairOptions}
                            onChange={(selectedValue: string) =>
                                onChangeEvent(selectedValue, 'repairOption')
                            }
                            forceDefault
                            defaultIfOneOption
                            addDefaultOption
                            defaultText={t('select_default')}
                        />
                        {repairRequest?.repairOption &&
                            repairRequest.repairOption !==
                                SelectInputHelper.NullValue() && (
                                <BaseAlert
                                    className='mt-3 mb-0'
                                    message={tyreRepairMessage}
                                    alertType={AlertTypes[AlertId.Warning]}
                                />
                            )}
                    </FormInput>
                )}
            </div>
            {isNonUsUser && (
                <>
                    {repairRequest?.damageType ===
                        nonUSTyreDamageTypes.Malicious && (
                        <FormInput
                            id='police-report'
                            label={t(
                                'tyre_repair_request.police_report_upload_title'
                            )}
                            helpText={t(
                                'tyre_repair_request.help_text.police_report'
                            )}
                            errorText={formErrors?.policeReport}>
                            <DocumentUploader
                                id={'police-report'}
                                documentIndex={0}
                                documentName={
                                    repairRequest?.policeReport?.filename
                                }
                                uploadedDocument={documentToImageFormat(
                                    repairRequest?.policeReport
                                )}
                                saveOnUpload={true}
                                onDocumentUploaded={onPoliceReportUpdate}
                            />
                        </FormInput>
                    )}
                    <FormInput
                        id='closeup-image-forminput'
                        label={t(
                            'tyre_repair_request.step_2_damage_upload_photo_title'
                        )}
                        helpText={t('tyre_repair_request.help_text.close_up')}
                        errorText={formErrors?.closeUpImage}>
                        <div className='images-wrapper'>
                            <div className='image-wrapper'>
                                <p
                                    className='example-image-text'
                                    style={{
                                        textAlign: 'center',
                                        fontWeight: 'bold'
                                    }}>
                                    {t(
                                        'tyre_repair_request.step_2_damage_upload_labels.example'
                                    )}
                                </p>
                                <img
                                    className='example-images'
                                    id='close-up-example-images'
                                    src={Images.TyreCloseup}
                                    alt='close up image of tyre damage'
                                />
                            </div>
                            <div className='image-wrapper'>
                                <div className='image-preview'>
                                    <div className='image-preview-header'>
                                        {t(
                                            'tyre_repair_request.step_2_damage_upload_labels.close_up_image'
                                        )}
                                    </div>
                                    <div className='image-preview-content'>
                                        <ImageUploader
                                            name='close-up-damage-preview'
                                            title={t('upload_image')}
                                            UploadedImage={
                                                repairRequest?.uploadedImages[0]
                                            }
                                            onImageChange={onUploadImageChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FormInput>
                    <FormInput
                        id='distance-image-forminput'
                        label={t(
                            'tyre_repair_request.upload_image_distance_label'
                        )}
                        helpText={helpTextDistance}
                        errorText={formErrors?.distanceImage}>
                        <div className='images-wrapper'>
                            <div className='image-wrapper'>
                                <p
                                    className='example-image-text'
                                    style={{
                                        textAlign: 'center',
                                        fontWeight: 'bold'
                                    }}>
                                    {t(
                                        'tyre_repair_request.step_2_damage_upload_labels.example'
                                    )}
                                </p>
                                <img
                                    className='example-images'
                                    id='damage-example-images'
                                    src={Images.TyreDistance}
                                    alt='distance image of tyre damage'
                                />
                            </div>
                            <div className='image-wrapper'>
                                <div className='image-preview'>
                                    <div className='image-preview-header'>
                                        {t(
                                            'tyre_repair_request.step_2_damage_upload_labels.image_from_1m_away'
                                        )}
                                    </div>
                                    <div className='image-preview-content'>
                                        <ImageUploader
                                            name='distance-damage-preview'
                                            title={t('upload_image')}
                                            UploadedImage={
                                                repairRequest?.uploadedImages[1]
                                            }
                                            onImageChange={onUploadImageChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FormInput>
                    <FormInput
                        id='additional-image-forminput'
                        label={t('tyre_repair_request.additional_images_title')}
                        helpText={t(
                            'tyre_repair_request.help_text.additional_images'
                        )}>
                        <div className='images-wrapper'>
                            <div className='image-wrapper'>
                                <div className='image-preview'>
                                    <div className='image-preview-header'>
                                        {t(
                                            'tyre_repair_request.additional_image1'
                                        )}
                                    </div>
                                    <div className='image-preview-content'>
                                        <ImageUploader
                                            name='additional-image-one-preview'
                                            title={t('upload_image')}
                                            UploadedImage={
                                                repairRequest?.uploadedImages[2]
                                            }
                                            onImageChange={onUploadImageChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='image-wrapper'>
                                <div className='image-preview'>
                                    <div className='image-preview-header'>
                                        {t(
                                            'tyre_repair_request.additional_image2'
                                        )}
                                    </div>
                                    <div className='image-preview-content'>
                                        <ImageUploader
                                            name='additional-image-two-preview'
                                            title={t('upload_image')}
                                            UploadedImage={
                                                repairRequest?.uploadedImages[3]
                                            }
                                            onImageChange={onUploadImageChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FormInput>
                </>
            )}
            <div className='card-footer page-content-footer'>
                {formErrors && (
                    <ErrorSummary
                        title={errorSummaryTitle}
                        errors={errorlist()}
                        className='card-error'
                    />
                )}
            </div>
            <div tabIndex={0} ref={modalBottom}></div>
        </div>
    );
};

export default TyreSpecificForm;
